import React from "react";
import { Search, CalendarDays, RefreshCw } from "lucide-react";

const inputClass =
  "w-full px-3 py-2.5 bg-white dark:bg-dark-100 border border-gray-200 dark:border-dark-200 rounded-lg text-sm text-gray-900 dark:text-light-100 focus:ring-2 focus:ring-primary-accent focus:border-transparent outline-none transition-all";

/**
 * Employee / date-range / status filters are SERVER-side behind an Apply button
 * (mirroring ArchivedBoard), while the name search is CLIENT-side so typing does
 * not refetch on every keystroke (mirroring AdminAttendance).
 *
 * A raw <select> is used rather than components/forms/Select, because that
 * component hardcodes a disabled empty-value placeholder which would swallow the
 * "All employees" option.
 */
const MonitoringFilters = ({
  employees,
  draft,
  onDraftChange,
  onApply,
  searchTerm,
  onSearchChange,
  loading,
}) => {
  const set = (patch) => onDraftChange({ ...draft, ...patch });

  return (
    <div className="mb-4 bg-white dark:bg-primary-card rounded-xl border border-gray-200 dark:border-dark-100 p-4">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        <div>
          <label className="block text-xs text-gray-500 dark:text-light-500 mb-1.5">
            Employee
          </label>
          <select
            value={draft.employeeId}
            onChange={(e) => set({ employeeId: e.target.value })}
            className={inputClass}
          >
            <option value="">All employees</option>
            {employees.map((emp) => (
              <option key={emp._id} value={emp._id}>
                {emp.fullNameEn}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-500 dark:text-light-500 mb-1.5">
            From
          </label>
          <input
            type="date"
            value={draft.startDate}
            onChange={(e) => set({ startDate: e.target.value })}
            className={inputClass}
          />
        </div>

        <div>
          <label className="block text-xs text-gray-500 dark:text-light-500 mb-1.5">
            To
          </label>
          <input
            type="date"
            value={draft.endDate}
            onChange={(e) => set({ endDate: e.target.value })}
            className={inputClass}
          />
        </div>

        <div>
          <label className="block text-xs text-gray-500 dark:text-light-500 mb-1.5">
            Status
          </label>
          <select
            value={draft.status}
            onChange={(e) => set({ status: e.target.value })}
            className={inputClass}
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="ended">Ended</option>
            <option value="interrupted">Interrupted</option>
          </select>
        </div>

        <div className="flex items-end">
          <button
            onClick={onApply}
            disabled={loading}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-semibold bg-primary-accent hover:bg-orange-600 disabled:opacity-50 text-white rounded-lg transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Apply
          </button>
        </div>
      </div>

      <div className="relative mt-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Filter the loaded rows by name…"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-dark-100 border border-gray-200 dark:border-dark-200 rounded-lg text-sm text-gray-900 dark:text-light-100 placeholder-gray-400 focus:ring-2 focus:ring-primary-accent focus:border-transparent outline-none transition-all"
        />
      </div>

      <p className="mt-2 text-xs text-gray-400 dark:text-light-500 flex items-center gap-1.5">
        <CalendarDays className="w-3.5 h-3.5" />
        Employee, dates and status are applied on the server. The name filter
        applies to the rows already loaded.
      </p>
    </div>
  );
};

export default MonitoringFilters;
